import { useState } from 'react'
import { Settings, Copy, Check, X, Plus, ScrollText, ListTodo } from 'lucide-react'
import type { Creds, SavedConfig } from './types'
import { deriveEnv } from './utils'

interface CredentialBarProps {
  creds: Creds
  onChange: (c: Creds) => void
  savedConfigs: SavedConfig[]
  activeConfigId: number | null
  onSelectConfig: (id: number) => void
  onSaveNew: (name: string) => Promise<void>
  onDeleteConfig: (id: number) => Promise<void>
  onClearConfig: () => void
  editorOpen: boolean
  onToggleEditor: () => void
  onTestConnection: () => Promise<'ok' | 'error'>
  onLogClick?: () => void
  onTodoClick?: () => void
  logCount?: number
  title?: string
  subtitle?: string
}

export function CredentialBar({
  creds,
  onChange,
  savedConfigs,
  activeConfigId,
  onSelectConfig,
  onSaveNew,
  onDeleteConfig,
  onClearConfig,
  editorOpen,
  onToggleEditor,
  onTestConnection,
  onLogClick,
  onTodoClick,
  logCount = 0,
  title = 'Testing Ground',
  subtitle = 'API verification console',
}: CredentialBarProps) {
  const [copiedCid, setCopiedCid] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [showNewInput, setShowNewInput] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<'ok' | 'error' | null>(null)

  // Configured = a saved credential set is actively selected
  const configured = activeConfigId !== null
  const env = deriveEnv(creds.apiBase)
  const activeConfig = savedConfigs.find(c => c.id === activeConfigId)

  function copyCid() {
    if (!creds.customerId) return
    void navigator.clipboard.writeText(creds.customerId)
    setCopiedCid(true)
    setTimeout(() => setCopiedCid(false), 1500)
  }

  async function handleSaveNew() {
    if (!saveName.trim()) return
    setSaving(true)
    try {
      await onSaveNew(saveName.trim())
      setSaveName('')
      setShowNewInput(false)
    } finally {
      setSaving(false)
    }
  }

  async function handleTestConnection() {
    setTesting(true)
    setTestResult(null)
    try {
      const r = await onTestConnection()
      setTestResult(r)
      setTimeout(() => setTestResult(null), 3000)
    } finally {
      setTesting(false)
    }
  }

  const inputCls = 'w-full bg-[#0a1322] border border-[#233149] rounded px-2.5 py-1.5 text-[12px] font-mono text-[#cbd5e1] placeholder:text-[#3f4f6b] focus:outline-none focus:border-[#6366f1]/50'
  const labelCls = 'block text-[10px] uppercase tracking-wider text-[#5b6b85] mb-1'

  return (
    <div
      className="sticky top-0 z-30 w-full"
      style={{
        background: 'rgba(13,20,36,.94)',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid #1e2c44',
        borderBottom: '1px solid #1e2c44',
      }}
    >
      {/* Main row */}
      <div className="flex items-center gap-3 px-5 py-[11px]">
        {/* Title group */}
        <div className="flex items-center gap-3 shrink-0">
          <div>
            <p className="text-[14px] font-bold text-[#f1f5f9] leading-none">{title}</p>
            <p className="text-[11px] text-[#475569] mt-0.5">{subtitle}</p>
          </div>
          <div className="w-px h-[26px] bg-[#1e2c44]" />
        </div>

        {/* Status pill */}
        <div
          className="flex items-center gap-2 px-3 py-[6px] rounded-[9px] shrink-0"
          style={{
            background: configured ? 'rgba(52,211,153,.08)' : 'rgba(248,113,113,.08)',
            border: configured ? '1px solid rgba(52,211,153,.25)' : '1px solid rgba(248,113,113,.25)',
          }}
        >
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{
              background: configured ? '#34d399' : '#f87171',
              boxShadow: configured ? '0 0 0 3px rgba(52,211,153,.2)' : '0 0 0 3px rgba(248,113,113,.2)',
              animation: configured ? 'none' : 'pulse 2s cubic-bezier(0.4,0,0.6,1) infinite',
            }}
          />
          <span className="text-[12px] font-semibold" style={{ color: configured ? '#86efac' : '#fca5a5' }}>
            {configured ? 'Credentials configured' : 'Credentials not configured'}
          </span>
        </div>

        {/* Env badge */}
        {configured && env && (
          <div
            className="px-2 py-0.5 rounded-[6px] text-[10px] font-bold uppercase tracking-wider shrink-0"
            style={
              env === 'UAT'
                ? { background: 'rgba(251,191,36,.1)', border: '1px solid rgba(251,191,36,.3)', color: '#fcd34d' }
                : { background: 'rgba(248,113,113,.1)', border: '1px solid rgba(248,113,113,.3)', color: '#fca5a5' }
            }
          >
            {env}
          </div>
        )}

        {/* Customer ID chip */}
        {configured && creds.customerId && (
          <button
            onClick={copyCid}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-[6px] shrink-0 transition-colors hover:border-[#3f5070]"
            style={{ background: '#0f1a2e', border: '1px solid #233149' }}
            title="Click to copy Customer ID"
          >
            <span className="text-[11px] text-[#5b6b85] font-mono">customer_id</span>
            <span className="text-[11px] font-mono text-[#94a3b8]">{creds.customerId}</span>
            {copiedCid
              ? <Check size={10} className="text-emerald-400 shrink-0" />
              : <Copy size={10} className="text-[#5b6b85] shrink-0" />}
          </button>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Saved credentials dropdown + controls */}
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] uppercase tracking-wider text-[#475569]">Saved credentials</span>

          {showNewInput ? (
            <div className="flex items-center gap-1.5">
              <input
                autoFocus
                value={saveName}
                onChange={e => setSaveName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') void handleSaveNew()
                  if (e.key === 'Escape') { setShowNewInput(false); setSaveName('') }
                }}
                placeholder="Config name…"
                className="bg-[#0a1322] border border-[#233149] rounded px-2 py-1 text-[11px] font-mono text-[#cbd5e1] placeholder:text-[#3f4f6b] focus:outline-none focus:border-[#6366f1]/50 w-32"
              />
              <button
                onClick={() => void handleSaveNew()}
                disabled={saving || !saveName.trim()}
                className="px-2 py-1 rounded text-[11px] bg-[#4f46e5] text-white disabled:opacity-40 hover:bg-[#6366f1] transition-colors"
              >
                {saving ? '…' : 'Save'}
              </button>
              <button onClick={() => { setShowNewInput(false); setSaveName('') }} className="text-[#5b6b85] hover:text-[#94a3b8]">
                <X size={12} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <select
                value={activeConfigId ?? ''}
                onChange={e => { const v = e.target.value; if (v) onSelectConfig(Number(v)) }}
                className="text-[12px] font-medium rounded-[7px] px-2.5 py-[5px] focus:outline-none cursor-pointer"
                style={{
                  background: '#0f1a2e',
                  border: '1px solid #233149',
                  color: activeConfigId ? '#c7d2fe' : '#5b6b85',
                  minWidth: 170,
                }}
              >
                <option value="" disabled>— Select credentials —</option>
                {savedConfigs.map(cfg => (
                  <option key={cfg.id} value={cfg.id}>{cfg.name}</option>
                ))}
              </select>

              {/* Delete active config */}
              {activeConfig && (
                <button
                  onClick={() => void onDeleteConfig(activeConfig.id)}
                  className="p-1.5 text-[#475569] hover:text-red-400 transition-colors rounded"
                  title={`Delete "${activeConfig.name}"`}
                >
                  <X size={12} />
                </button>
              )}

              {/* New credential + button — clears fields and opens editor */}
              <button
                onClick={() => {
                  onChange({ ...creds, customerId: '', childCustomerId: '', apiKey: '' })
                  onClearConfig()
                  if (!editorOpen) onToggleEditor()
                }}
                className="flex items-center justify-center w-[28px] h-[28px] rounded-[7px] text-[#5b6b85] hover:text-[#94a3b8] transition-colors shrink-0"
                style={{ border: '1px dashed #2a3a57' }}
                title="Add new credentials"
              >
                <Plus size={13} />
              </button>
            </div>
          )}
        </div>

        {/* Log button */}
        {onLogClick && (
          <button
            onClick={onLogClick}
            className="relative flex items-center gap-1.5 px-2.5 py-[5px] rounded-[8px] text-[12px] transition-colors shrink-0"
            style={{ background: 'rgba(99,102,241,.06)', border: '1px solid rgba(99,102,241,.15)', color: '#6b7280' }}
            title="View API log"
          >
            <ScrollText size={13} />
            Log
            {logCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 text-[9px] font-bold text-white">
                {logCount > 9 ? '9+' : logCount}
              </span>
            )}
          </button>
        )}

        {/* TODO button */}
        {onTodoClick && (
          <button
            onClick={onTodoClick}
            className="flex items-center gap-1.5 px-2.5 py-[5px] rounded-[8px] text-[12px] transition-colors shrink-0"
            style={{ background: 'rgba(99,102,241,.06)', border: '1px solid rgba(99,102,241,.15)', color: '#6b7280' }}
            title="View development TODO list"
          >
            <ListTodo size={13} />
          </button>
        )}

        {/* Gear / Credentials button */}
        <button
          onClick={onToggleEditor}
          className="flex items-center gap-1.5 px-2.5 py-[5px] rounded-[8px] text-[12px] transition-colors shrink-0"
          style={editorOpen
            ? { background: 'rgba(99,102,241,.2)', border: '1px solid rgba(99,102,241,.4)', color: '#c7d2fe' }
            : { background: 'rgba(99,102,241,.08)', border: '1px solid rgba(99,102,241,.2)', color: '#818cf8' }
          }
        >
          <Settings size={13} />
          Credentials
        </button>
      </div>

      {/* Expandable editor */}
      {editorOpen && (
        <div
          className="border-t px-5 py-4"
          style={{ background: '#0d1626', borderColor: '#1e2c44', animation: 'fadeUp .18s ease' }}
        >
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Customer ID</label>
              <input
                value={creds.customerId}
                onChange={e => onChange({ ...creds, customerId: e.target.value })}
                placeholder="your-customer-id"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Child Customer ID</label>
              <input
                value={creds.childCustomerId}
                onChange={e => onChange({ ...creds, childCustomerId: e.target.value })}
                placeholder="Only if account is a child account"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>API Key</label>
              <input
                value={creds.apiKey}
                onChange={e => onChange({ ...creds, apiKey: e.target.value })}
                placeholder="your-api-key"
                className={inputCls}
              />
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={() => setShowNewInput(true)}
              className="px-3 py-1.5 rounded text-[12px] font-medium bg-[#4f46e5] text-white hover:bg-[#6366f1] transition-colors"
            >
              Save credential set
            </button>
            <button
              onClick={onToggleEditor}
              className="px-3 py-1.5 rounded text-[12px] font-medium text-[#94a3b8] hover:text-[#f1f5f9] transition-colors"
              style={{ background: '#111c30', border: '1px solid #1e2c44' }}
            >
              Close
            </button>
            {activeConfigId !== null && (
              <button
                onClick={() => onClearConfig()}
                className="px-3 py-1.5 rounded text-[12px] text-[#94a3b8] hover:text-red-400 transition-colors"
              >
                Clear
              </button>
            )}
            <div className="flex-1" />
            <button
              onClick={() => void handleTestConnection()}
              disabled={testing || !creds.customerId || !creds.apiKey}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-[12px] font-medium transition-colors disabled:opacity-40"
              style={{ background: 'rgba(52,211,153,.12)', border: '1px solid rgba(52,211,153,.3)', color: '#34d399' }}
            >
              {testing ? '…' : testResult === 'ok' ? '✓ Connected' : testResult === 'error' ? '✗ Failed' : 'Test connection · /ruok'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
